import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Sparkles,
  Send,
  RefreshCw,
  Dumbbell,
  Apple,
  Moon,
  Zap,
  MessageSquare,
  Loader2,
  ChevronRight,
  X,
} from 'lucide-react';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';

export default function AICoachPage() {
  const INITIAL_MESSAGE = {
    id: 'welcome',
    role: 'assistant',
    content: "Hey! I'm your AI Fitness Coach powered by Gemini. I can help you with workout routines, nutrition advice, recovery strategies, and more. What would you like to work on today?",
  };

  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const messagesEndRef = useRef(null);

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';

  const quickActions = [
    { icon: Dumbbell, label: 'Workout tips', message: 'Give me tips to improve my workout today' },
    { icon: Apple, label: 'Nutrition', message: 'What should I eat before my workout?' },
    { icon: Moon, label: 'Recovery', message: 'How can I improve my recovery?' },
    { icon: Zap, label: 'Motivation', message: 'I need some motivation to work out' },
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/chats`);
      if (!response.ok) throw new Error('Failed to load chat history');
      const data = await response.json();
      setChatHistory(data.result || []);
    } catch (err) {
      console.error('[AICoach] Failed to load chat history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [userId]);

  useEffect(() => { loadChatHistory(); }, [loadChatHistory]);

  const saveChat = async (messagesToSave) => {
    try {
      const chatData = {
        title: messagesToSave.find((m) => m.role === 'user')?.content?.slice(0, 60) || 'New Chat',
        messages: messagesToSave.filter((m) => m.id !== 'welcome'),
        messageCount: messagesToSave.length,
      };
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chatData),
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentChatId(data.result?.id);
        loadChatHistory();
      }
    } catch (err) {
      console.error('[AICoach] Failed to save chat:', err);
    }
  };

  const loadChat = (chat) => {
    setMessages([INITIAL_MESSAGE, ...(chat.messages || [])]);
    setCurrentChatId(chat.id);
    setShowHistory(false);
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = { id: Date.now(), role: 'user', content: messageText };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);
    setError(null);

    try {
      const history = messages
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, history }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || errData.details || `Server error (${response.status})`);
      }

      const contentType = response.headers.get('content-type');

      if (contentType?.includes('text/event-stream')) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        const assistantId = Date.now() + 1;
        setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const eventData = JSON.parse(line.slice(6));
                if (eventData.type === 'chunk' && eventData.content) {
                  fullContent += eventData.content;
                  setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: fullContent } : m));
                } else if (eventData.type === 'complete' && eventData.fullContent) {
                  fullContent = eventData.fullContent;
                  setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: fullContent } : m));
                } else if (eventData.type === 'error') {
                  throw new Error(eventData.error);
                }
              } catch (parseErr) { /* skip */ }
            }
          }
        }
        setIsTyping(false);
        saveChat([...updatedMessages, { id: assistantId, role: 'assistant', content: fullContent }]);
      } else {
        const data = await response.json();
        const responseText = data.data?.response || data.response || data.data?.fullContent || 'Unable to generate a response. Please try again.';
        const assistantMessage = { id: Date.now() + 1, role: 'assistant', content: responseText };
        const finalMessages = [...updatedMessages, assistantMessage];
        setMessages(finalMessages);
        setIsTyping(false);
        saveChat(finalMessages);
      }
    } catch (err) {
      console.error('[AICoach] Send error:', err);
      setError(err.message || 'Failed to get response from AI coach.');
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: `Connection issue: ${err.message}. Please try again.` },
      ]);
    }
  };

  const clearChat = () => {
    setMessages([{ id: 'welcome', role: 'assistant', content: "Chat cleared! How can I help you with your fitness journey today?" }]);
    setCurrentChatId(null);
    setError(null);
  };

  return (
    <div className="min-h-full bg-black text-white flex flex-col pb-16">

      {/* Compact Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b border-[#1F1F1F] flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-amber-400" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">AI</p>
            <h1 className="text-[15px] font-semibold tracking-tight leading-none">Fitness Coach</h1>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="relative p-1.5 rounded-lg bg-white/[0.04] border border-[#1F1F1F]"
          >
            <MessageSquare size={13} className="text-zinc-400" />
            {chatHistory.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-400 text-black rounded-full text-[8px] flex items-center justify-center font-bold">
                {Math.min(chatHistory.length, 9)}
              </span>
            )}
          </button>
          <button onClick={clearChat} className="p-1.5 rounded-lg bg-white/[0.04] border border-[#1F1F1F]">
            <RefreshCw size={13} className="text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Chat History Dropdown */}
      {showHistory && (
        <div className="border-b border-[#1F1F1F] bg-[#0A0A0A] flex-shrink-0">
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Previous Chats</p>
              <button onClick={() => setShowHistory(false)}>
                <X size={12} className="text-zinc-500" />
              </button>
            </div>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 size={14} className="text-amber-400 animate-spin" />
              </div>
            ) : chatHistory.length === 0 ? (
              <p className="text-[11px] text-zinc-600 py-2">No previous chats</p>
            ) : (
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {chatHistory.slice(0, 20).map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => loadChat(chat)}
                    className={`w-full text-left px-2 py-1.5 rounded-lg flex items-center justify-between ${
                      currentChatId === chat.id
                        ? 'border border-amber-400/30 bg-amber-400/10'
                        : 'border border-[#1F1F1F] bg-white/[0.02]'
                    }`}
                  >
                    <div className="truncate flex-1 mr-2">
                      <p className="text-[11px] text-zinc-200 truncate">{chat.title || 'Untitled Chat'}</p>
                      <p className="text-[9px] text-zinc-600">
                        {chat.messageCount || chat.messages?.length || 0} messages
                      </p>
                    </div>
                    <ChevronRight size={12} className="text-zinc-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-3 mt-2 rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 flex-shrink-0">
          {error}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 1 && (
          <div className="mb-3">
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2">Quick actions</p>
            <div className="flex flex-wrap gap-1.5">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={() => handleSend(action.message)}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-xl border border-[#1F1F1F] bg-white/[0.03] text-[11px] text-zinc-300"
                  >
                    <Icon size={11} className="text-amber-400" />
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[88%] px-3 py-2 rounded-2xl text-[12px] leading-relaxed ${
                message.role === 'user'
                  ? 'border border-amber-400/45 bg-amber-500/15 text-amber-100 rounded-br-sm'
                  : 'border border-[#1F1F1F] bg-[#0A0A0A] text-zinc-200 rounded-bl-sm'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex items-center gap-1 mb-1">
                  <Sparkles size={10} className="text-amber-400" />
                  <span className="text-[9px] uppercase tracking-widest text-amber-400">AI Coach</span>
                </div>
              )}
              <p className="whitespace-pre-wrap">{message.content || (isTyping ? '...' : '')}</p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2 rounded-2xl rounded-bl-sm">
              <div className="flex items-center gap-2">
                <Sparkles size={10} className="text-amber-400" />
                <div className="flex gap-1">
                  {[0, 150, 300].map((delay) => (
                    <div
                      key={delay}
                      className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce"
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 pb-3 pt-2 border-t border-[#1F1F1F] flex-shrink-0">
        <div className="flex gap-1.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Ask your coach anything..."
            className="flex-1 px-3 py-2 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[12px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400"
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || isTyping}
            className="h-[38px] w-[38px] flex items-center justify-center rounded-xl border border-amber-400/50 bg-amber-500/15 text-amber-200 disabled:opacity-45"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
